import {
  useQuery,
  type UseQueryOptions,
  type QueryClient,
} from '@tanstack/react-query';
import { StatusCode } from '@hungryroot/rest';
import {
  type Params as GetApiV2CustomersCustomerIdRatablePairingsParams,
  getApiV2CustomersCustomerIdRatablePairings,
  type PaginatedRatablePairingListList,
} from '@hungryroot/rest/api/v2/customers/[id]/ratable_pairings/get';
import { useIdentify } from '@hungryroot/rest-hooks';

// All of these fields are returned by the API
// But we are omitting them here because the UI doesn't actually need them
// If the UI needs these someday, it is safe to remove them BUT
// Be prepared to update some mocks and stories.
export type PairingIngredient = Omit<
  PaginatedRatablePairingListList['results'][number]['ingredients'][number],
  | 'is_sweet'
  | 'is_kid_snack'
  | 'packaged_image_url'
  | 'volume'
  | 'points'
  | 'tags'
  | 'is_snack'
  | 'brand_name'
  | 'is_ambient'
  | 'is_breakfast'
  | 'is_fruit'
>;
export interface RatablePairing
  extends Omit<
    PaginatedRatablePairingListList['results'][number],
    'ingredients'
  > {
  ingredients: PairingIngredient[];
}

type OptionsType = Omit<
  UseQueryOptions<
    PaginatedRatablePairingListList,
    unknown,
    PaginatedRatablePairingListList
  >,
  'initialData' | 'queryFn' | 'queryKey'
>;

const getRatablePairingsQueryKey = (
  customerId: number,
  queryParams: UseRatablePairingsProps['queryParams']
) => {
  const returnedKey = ['Pairings', 'ratable pairings', customerId];

  if (queryParams) {
    return [...returnedKey, queryParams];
  }

  return returnedKey;
};

interface InvalidateRatablePairingsArgs {
  client: QueryClient;
  customerId: number;
  queryParams?: UseRatablePairingsProps['queryParams'];
}

export function invalidateRatablePairings({
  client,
  customerId,
  queryParams,
}: InvalidateRatablePairingsArgs) {
  client.invalidateQueries({
    queryKey: getRatablePairingsQueryKey(customerId, queryParams),
  });
}

type UseRatablePairingsProps = {
  id: number;
  options?: OptionsType;
  queryParams?: Omit<
    GetApiV2CustomersCustomerIdRatablePairingsParams,
    'customerId'
  >;
};

function useRatablePairings(params: UseRatablePairingsProps) {
  const { id, options, queryParams } = params;

  return useQuery({
    queryKey: getRatablePairingsQueryKey(id, queryParams),
    queryFn: async () => {
      const resp = await getApiV2CustomersCustomerIdRatablePairings({
        customerId: id,
        ...queryParams,
      });

      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    ...options,
    enabled: options?.enabled === false || id === 0 ? false : true,
  });
}

export function useSelfRatablePairings(
  params?: Omit<UseRatablePairingsProps, 'id'>
) {
  const { customerData } = useIdentify();

  return useRatablePairings({
    ...params,
    id: customerData?.id ?? 0,
  });
}
